/**
 * Mobile floating action button for recording.
 *
 * Fixed bottom-right, big red mic circle (hidden on desktop).
 * During recording: shows dismiss + timer pill + pause/stop.
 */

import { useRecordingStore, formatElapsed } from '@/stores/recording'
import { useRecordingPrefs } from '@/stores/recordingPrefs'
import { useNavigate, useLocation } from 'react-router'
import { Mic, MicOff, Square, Pause, Play, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { CaptureLostNotice } from './CaptureLostNotice'

export function RecordingFAB() {
  const recording = useRecordingStore()
  const navigate = useNavigate()
  const location = useLocation()

  const handleStart = () => {
    if (location.pathname !== '/') {
      navigate('/')
    }
    const profileId = useRecordingPrefs.getState().effectiveProfileId() ?? undefined
    setTimeout(() => recording.startRecording({ profileId }), location.pathname !== '/' ? 100 : 0)
  }

  // Hide on desktop
  if (recording.state === 'idle') {
    return (
      <button
        onClick={handleStart}
        className="lg:hidden fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full bg-record hover:bg-record-hover flex items-center justify-center shadow-lg transition-colors"
        aria-label="Start recording"
      >
        <Mic className="w-6 h-6 text-on-record" />
      </button>
    )
  }

  if (recording.state === 'processing') {
    return (
      <div className="lg:hidden fixed bottom-6 right-6 z-30 flex items-center gap-2 bg-surface rounded-full shadow-lg border border-border px-4 py-2.5">
        <div className="w-4 h-4 border-2 border-claro-600/30 border-t-claro-600 rounded-full animate-spin" />
        <span className="text-xs font-medium text-ink">Processing...</span>
      </div>
    )
  }

  // Recording or paused
  const showAudioWarning = recording.audioWarning && recording.state === 'recording'
  const lost = recording.captureLost
  return (
    <div className="lg:hidden fixed bottom-6 right-4 z-30 flex flex-col items-end gap-2">
      {lost && (
        <CaptureLostNotice
          compact
          reason={lost}
          elapsed={recording.elapsed}
          onFinish={() => recording.stopRecording()}
          onBackup={recording.backupCapturing}
        />
      )}
      {showAudioWarning && !lost && (
        <div
          role="alert"
          className="flex items-center gap-1.5 bg-warning/15 border border-warning/30 rounded-full shadow-lg px-3 py-1.5"
        >
          <MicOff className="w-3.5 h-3.5 text-warning shrink-0" />
          <span className="text-[11px] font-medium text-on-warning-soft">No audio detected</span>
        </div>
      )}
      <div className="flex items-center gap-2 bg-surface rounded-full shadow-lg border border-border pl-3 pr-2 py-1.5">
        <span
          className={cn(
            'w-2 h-2 rounded-full',
            showAudioWarning || lost ? 'bg-warning' : 'bg-rec recording-pulse',
          )}
        />
        <span className="text-xs font-medium text-ink tabular-nums">
          {formatElapsed(recording.elapsed)}
        </span>

      <button
        onClick={() => recording.cancelRecording()}
        className="p-1.5 rounded-full hover:bg-surface-tertiary text-ink-faint transition-colors"
        title="Discard"
        aria-label="Discard recording"
      >
        <X className="w-4 h-4" />
      </button>

      {recording.state === 'recording' ? (
        <button
          onClick={() => recording.pauseRecording()}
          className="p-1.5 rounded-full hover:bg-surface-tertiary text-ink-secondary transition-colors"
          title="Pause"
          aria-label="Pause recording"
        >
          <Pause className="w-4 h-4" />
        </button>
      ) : (
        <button
          onClick={() => recording.resumeRecording()}
          className="p-1.5 rounded-full hover:bg-claro-100 text-claro-700 transition-colors"
          title="Resume"
          aria-label="Resume recording"
        >
          <Play className="w-4 h-4" />
        </button>
      )}

      <button
        onClick={() => recording.stopRecording()}
        className="w-10 h-10 rounded-full bg-record hover:bg-record-hover flex items-center justify-center transition-colors"
        title="Stop & save"
        aria-label="Stop and save recording"
      >
        <Square className="w-4 h-4 text-on-record" />
      </button>
      </div>
    </div>
  )
}
